import React, { useState, useEffect, useCallback } from 'react';
import Entity from '../Common/Entity';
import { Box, Typography, Grid, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PODetailsForm from '../PO/Components/PODetailsForm';
import ProductList from '../PO/Components/ProductList';
import GlassModal from '../../v2/MUI_custom_component/Modal/GlassModal';
import { CustomButton } from '../../v2/MUI_custom_component';
import PurchaseOrdersSection from './components/PurchaseOrdersSection';
import DispatchStatusSection from './components/DispatchStatusSection';
import {
  CreateOrderAction,
  ModifyOrderAction,
  PoBaseProductDetailsAction,
  getpaginatePOAction,
} from '../PO/API/Action';
import { getpaginateDPAction } from '../DP/DPDetails/API/Action';
import { getInvoicePaginatedAction } from '../Invoice/API/Action';
import InvoiceCardComponent from './components/InvoiceCardComponent';
import EditPOModal from '../PO/Components/EditPOModal';
import InventoryCompanyCard from '../Inventory/components/InventoryCompanyCard';
import BusinessExpiryInventoryCard from './components/BusinessExpiryInventoryCard';
import { trackAnalyticsEvent } from '../../utils/clevertap';
import { GetCompanyWiseInventoryData } from '../../screen/distributor/action/util_action';
import { CompaniesBaseProductPrice } from '../../screen/auth/action/util_actions';
import IllustrationWrapper from '../Illustrration/IllustrationWrapper';
import NoDataFoundSvg from '../Illustrration/components/NoDataFoundSvg';

const PAGE_SIZE = 5;

const MyBusiness = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [businessData, setBusinessData] = useState({
    poList: [],
    dpList: [],
    invoicesData: [],
    baseProduct: [],
    inventoryData: null, 
    companies: [],
    minMaxMoqAndMov: null,
  });

  const [productDetails, setProductDetails] = useState([]);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [poDetails, setPODetails] = useState({ po_number: '', quantity: '' });
  const [quantityError, setQuantityError] = useState('');
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [openEditPOModal, setOpenEditPOModal] = useState(false);
  const [editPODetails, setEditPODetails] = useState(null);

  const {
    selectedSupplier: selectedManufacturer,
    selectedEntity: selectedCompanyEntity,
    selectedSupplierData,
  } = useSelector((state) => state.entityReducer);

  const canCreatePO =
    selectedSupplierData?.is_secondary_supplier_required === false &&
    selectedSupplierData?.po_config?.as_seller?.required === true;

  const showPurchaseSection =
    selectedSupplierData?.po_config?.as_seller?.required;
  const showDispatchSection =
    selectedSupplierData?.dp_config?.as_seller?.required;

  const fetchAllData = useCallback(async () => {
    if (!selectedManufacturer || !selectedCompanyEntity) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const poConfig = new URLSearchParams();
    poConfig.append('f', 'id$gte$0');
    poConfig.append('o', 'id$desc');
    poConfig.append('page', '1');
    poConfig.append('page_size', PAGE_SIZE.toString());
    poConfig.append('is_buyer', 'true');
    poConfig.append('f', `buyer_esi_id$eq$${selectedCompanyEntity}`);

    const invoiceConfig = new URLSearchParams();
    invoiceConfig.append('f', 'id$gte$0');
    invoiceConfig.append('o', 'id$desc');
    invoiceConfig.append('page', '1');
    invoiceConfig.append('page_size', PAGE_SIZE.toString());
    invoiceConfig.append('f', `company_esi_id$eq$${selectedCompanyEntity}`);

    dispatch({ type: 'RESET_BCM_ORDER' });
    dispatch({ type: 'RESET_FMCG_REDUCER' });

    trackAnalyticsEvent('INVENTORY_PAGE_LOADED', {
      screen_name: 'inventory_main',
      action: 'page_load',
      company_esi_id: selectedCompanyEntity,
    });

    try {
      const results = await Promise.allSettled([
        CompaniesBaseProductPrice(selectedManufacturer, selectedCompanyEntity),
        PoBaseProductDetailsAction(selectedManufacturer, selectedCompanyEntity),
        getpaginatePOAction(poConfig),
        getpaginateDPAction(poConfig),
        GetCompanyWiseInventoryData(selectedCompanyEntity),
        getInvoicePaginatedAction(invoiceConfig),
      ]);

      const baseProduct =
        results[0]?.status === 'fulfilled' && !results[0]?.value?.error
          ? (results[0]?.value?.data ?? [])
          : [];

      const productDetailsResult =
        results[1]?.status === 'fulfilled' && !results[1]?.value?.error
          ? (results[1]?.value?.data ?? [])
          : [];
      setProductDetails(productDetailsResult);
      if (productDetailsResult?.products?.length === 1) {
        setSelectedProductId(productDetailsResult.products[0].id);
      } else if (productDetailsResult?.products?.length > 1) {
        setSelectedProductId(null);
      }

      const poApiResponse =
        results[2]?.status === 'fulfilled' && !results[2]?.value?.error
          ? (results[2]?.value?.data ?? {})
          : {};
      const poList = Array.isArray(poApiResponse?.po_list)
        ? poApiResponse.po_list
        : [];
      const minMaxMoqAndMov = poApiResponse?.min_max_moq_and_mov ?? null;

      const dpList =
        results[3]?.status === 'fulfilled' && !results[3]?.value?.error
          ? (results[3]?.value?.data ?? [])
          : [];

      let inventoryData = null;
      let companies = [];
      const inventoryResult = results[4];
      if (
        inventoryResult?.status === 'fulfilled' &&
        !inventoryResult?.value?.error &&
        inventoryResult?.value?.data?.length > 0
      ) {
        const invData = inventoryResult.value.data;
        companies = invData;
        let index = invData.findIndex(
          (ele) => ele.company_id == selectedManufacturer,
        );
        if (index === -1) index = 0;
        inventoryData = invData[index];

        dispatch({ type: 'SET_SELLER_ID', data: inventoryData?.company_id });
        dispatch({ type: 'SET_COMPANY_ESI_ID', data: selectedCompanyEntity });

        trackAnalyticsEvent('INVENTORY_DATA_LOADED', {
          screen_name: 'inventory_main',
          companies_count: invData.length,
          selected_company_id: inventoryData?.company_id,
          selected_company_name: inventoryData?.company_name,
        });
      } else {
        trackAnalyticsEvent('INVENTORY_DATA_LOAD_FAILED', {
          screen_name: 'inventory_main',
          error_message: 'No data available',
        });
      }

      const allInvoices =
        results[5]?.status === 'fulfilled' && !results[5]?.value?.error
          ? (results[5]?.value?.data ?? [])
          : [];

      setBusinessData({
        baseProduct,
        poList,
        dpList,
        invoicesData: allInvoices,
        inventoryData,
        companies,
        minMaxMoqAndMov,
      });
    } catch (error) {
      console.error('Business data fetch failed', error);
    } finally {
      setLoading(false);
    }
  }, [selectedManufacturer, selectedCompanyEntity, dispatch]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const hasProductPrice =
    showPurchaseSection && businessData.baseProduct?.length > 0;

  const hasPurchaseOrders = businessData.poList.length > 0;

  const hasInventory = !!businessData.inventoryData;

  const hasInvoices = businessData.invoicesData.length > 0;

  const hasAnySection =
    hasProductPrice ||
    hasPurchaseOrders ||
    businessData.dpList.length > 0 ||
    hasInventory ||
    hasInvoices;

  const validateQuantity = (value) => {
    if (!value || value.trim() === '') {
      return 'Quantity is required';
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return 'Quantity must be a valid number';
    }
    if (numValue <= 0) {
      return 'Quantity must be greater than 0';
    }
    if (productDetails?.limits_info) {
      const { min_moq, max_moq } = productDetails.limits_info;
      if (min_moq && numValue < min_moq) {
        return `Quantity must be at least ${min_moq} tons (minimum MOQ)`;
      }
      if (max_moq && numValue > max_moq) {
        return `Quantity cannot exceed ${max_moq} tons (maximum MOQ)`;
      }
    }
    return '';
  };

  const handleCreateOrder = async () => {
    setLoading(true);
    const selectedProduct =
      productDetails.products.length === 1
        ? productDetails.products[0]
        : productDetails.products.find(
            (product) => product.id === selectedProductId,
          );

    if (!selectedProduct) {
      toast.error('Please select a product');
      setLoading(false);
      return;
    }

    const productList = [
      {
        product_id: selectedProduct.id,
        quantity: {
          quantity_in_metric_ton: parseFloat(poDetails.quantity),
          multiplier: selectedProduct.multiplier,
        },
      },
    ];

    const orderData = {
      user_po_number: poDetails.po_number,
      seller_id: selectedManufacturer,
      buyer_esi_id: selectedCompanyEntity,
      product_list: productList,
    };

    const { error, message } = await CreateOrderAction(orderData);
    setLoading(false);

    if (!error) {
      toast.success('Purchase Order created successfully!');
      setPODetails({ po_number: '', quantity: '' });
      setOpenProductDialog(false);
      navigate('/complete-po');
    } else {
      toast.error(message || 'Failed to create purchase order');
    }
  };

  const onPressEditPO = (po) => {
    setEditPODetails(po);
    setOpenEditPOModal(true);
  };

  const handleEditOrder = async (editData) => {
    const payload = {
      order_booking_id: editData.poDetails?.humbee_po_number,
      user_po_number: editData.userPONumber,
      user_esi_id: selectedCompanyEntity,
      product_list: [
        {
          product_id: editData.poDetails.quantity_info[0].product_id,
          quantity: {
            quantity_in_metric_ton: Number(editData.quantity),
            multiplier: editData.poDetails.quantity_info[0].mulitplier_id,
          },
        },
      ],
    };
    const { error, message } = await ModifyOrderAction(
      payload,
      selectedCompanyEntity,
    );
    if (!error) {
      toast.success(message);
    } else {
      toast.error(message);
    }
    setOpenEditPOModal(false);
    fetchAllData();
  };

  return (
    <>
      <Box
        sx={{
          // p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: { xs: 2, md: 0 },
          mr: { xs: 0, md: 4 },
          ml: { xs: 0, md: 2 },
          pb: { xs: 0, md: 2 },
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: { xs: '0px 2px 8px 0px #0000001A', md: 'none' },
          background: { xs: '#FAFAFA', md: 'transparent' },
        }}
      >
         <Grid container>
          <Grid item xs={12} lg={6}>
            <Entity
              entityLabel='SAP Code*'
              showEntitySelect={true}
              forceRefresh={true}
              gridSizes={{
                supplier: { xs: 12, sm: 12 },
                entity: { xs: 12, sm: 12 },
              }}
            />
          </Grid>
          </Grid>
         </Box>
          {!loading && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  pl: { xs: 2, md: 3 },
                  mt: 2,
                  paddingBottom: 2,
                }}
              >
                {hasProductPrice && (
                  <ProductList
                    productDetails={businessData.baseProduct.slice(0, 1)}
                    selectedProductId={selectedProductId}
                    setSelectedProductId={setSelectedProductId}
                    showPOButton={canCreatePO && hasPurchaseOrders}
                    onCreatePO={() => navigate('/create-po')}
                  />
                )}
              </Box>
            )}
          {!loading && canCreatePO && !hasPurchaseOrders && (
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 0.5, md: 1 },
              }}
            >
              <Typography variant='h2'>Create Purchase Order</Typography>
              <PODetailsForm
                poDetails={poDetails}
                setPODetails={setPODetails}
                quantityError={quantityError}
                setQuantityError={setQuantityError}
                productDetails={productDetails}
                selectedProductId={selectedProductId}
                validateQuantity={validateQuantity}
                onCreatePO={() => setOpenProductDialog(true)}
                title='Create Purchase Order'
              />
            </Grid>
          )}
      <Box
        sx={{
          pl: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          paddingBottom: 2,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 8,
            }}
          >
            <CircularProgress />
          </Box>
        ) : hasAnySection ? (
          <>
            {showPurchaseSection && (
              <PurchaseOrdersSection
                poList={businessData.poList}
                onEditPO={onPressEditPO}
                MoQData={businessData?.minMaxMoqAndMov}
              />
            )}

            {showDispatchSection && (
              <DispatchStatusSection dpList={businessData.dpList} />
            )}

            <InvoiceCardComponent invoiceData={businessData.invoicesData} />

            {hasInventory && (
              <>
                <Typography variant='h2'>Inventory</Typography>
                {(() => {
                  const expiringInfo = businessData.inventoryData?.expiring_quantity_info;
                  const isExpiryCardVisible = expiringInfo
                    ? Object.values(expiringInfo).every((v) => v !== null)
                    : false;

                  if (isExpiryCardVisible) {
                    return (
                      <BusinessExpiryInventoryCard
                        bagText={expiringInfo?.expiring_quantity_string ?? '--'}
                        expiryDaysText={expiringInfo?.days_to_expiry ?? '--'}
                        onViewInventory={() =>
                          navigate('/inventory/company-inventory', {
                            state: { companyId: selectedManufacturer },
                          })
                        }
                      />
                    );
                  }

                  return (
                    <Box
                      sx={{
                        pr: 2,
                      }}
                    >
                      <InventoryCompanyCard
                        logo={businessData.inventoryData?.company_logo}
                        name={businessData.inventoryData?.company_name ?? '--'}
                        date={
                          businessData.inventoryData?.last_invoice_date || '-'
                        }
                        unit={
                          businessData.inventoryData?.total_quantity?.unit ||
                          '-'
                        }
                        availableQty={
                          businessData.inventoryData?.allocable_quantity
                            ?.quantity_in_metric_ton ?? '--'
                        }
                        unverifiedQty={
                          businessData.inventoryData?.blocked_quantity
                            ?.quantity_in_metric_ton
                        }
                        blockQty={
                          businessData.inventoryData?.blocked_quantity === null
                        }
                        badgeText={businessData.inventoryData?.expiry_badge}
                        actionButton={
                          <CustomButton
                            variant='filled'
                            color='primary'
                            label='View Inventory'
                            onClick={() =>
                              navigate('/inventory/company-inventory', {
                                state: {
                                  companyId: selectedManufacturer,
                                  entityId: selectedCompanyEntity,
                                },
                              })
                            }
                          />
                        }
                      />
                    </Box>
                  );
                })()}
              </>
            )}
          </>
        ) : (
          <Box
            sx={{
              mt: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <IllustrationWrapper
              illustration={<NoDataFoundSvg />}
              title='No Data Found'
              subtitle='No business data available yet.'
            />
          </Box>
        )}
      </Box>

      {/* Create PO Confirmation Modal */}
      <GlassModal
        open={openProductDialog}
        onClose={() => setOpenProductDialog(false)}
        title='Confirm Details'
        scrollable={true}
        maxWidth='600px'
        zIndex={2}
        maxHeight='45%'
        topMobile='45%'
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, md: 4 },
            mb: { xs: 4, sm: 0 },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant='labelM' sx={{ color: '#666' }}>
                SAP Code*
              </Typography>
              <Typography variant='Body1'>
                {selectedSupplierData &&
                  selectedSupplierData.entity_info[0].external_system_code}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant='labelM' sx={{ color: '#666' }}>
                Quantity*
              </Typography>
              <Typography variant='Body1'>
                {poDetails.quantity ? `${poDetails.quantity} Ton` : '-'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant='labelM' sx={{ color: '#666' }}>
                User PO No.
              </Typography>
              <Typography variant='Body1'>
                {poDetails.po_number || '-'}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant='h2'>
              Are you sure you want to proceed?
            </Typography>
            <Typography
              variant='body1'
              sx={{ color: '#666', textAlign: 'center' }}
            >
              Your purchase order will be created and sent to manufacturer for
              approval.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            <CustomButton
              label='No'
              variant='outlined'
              color='primary'
              onClick={() => setOpenProductDialog(false)}
              sx={{
                width: '50%',
              }}
            />
            <CustomButton
              label='Yes'
              variant='filled'
              color='primary'
              loading={loading}
              onClick={() => handleCreateOrder()}
              sx={{
                width: '50%',
              }}
            />
          </Box>
        </Box>
      </GlassModal>
      <EditPOModal
        open={openEditPOModal}
        onClose={() => setOpenEditPOModal(false)}
        poDetails={editPODetails}
        selectedSupplierData={selectedSupplierData}
        onSave={handleEditOrder}
        loading={loading}
      />
    </>
  );
};

export default MyBusiness;